import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  User,
  Mail,
  Phone,
  Palette,
  Star,
  Plus,
  Store,
  X,
  Eye,
  EyeOff,
  Trash2,
} from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { apiClient } from '../utils/api';
import { type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';
import { TimeInput } from '../components/TimeInput';
import { useCurrentShop } from '../contexts/CurrentShopContext';
import { ALL_SHOPS_ID } from '../utils/currentShop';

interface TimeSlot {
  open: string;
  close: string;
}

type ScheduleDay = TimeSlot[];

interface WeeklySchedule {
  monday?: ScheduleDay;
  tuesday?: ScheduleDay;
  wednesday?: ScheduleDay;
  thursday?: ScheduleDay;
  friday?: ScheduleDay;
  saturday?: ScheduleDay;
  sunday?: ScheduleDay;
}

type DayKey = keyof WeeklySchedule;

const planningDays: Array<{ key: DayKey; label: string }> = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' },
];

interface PractitionerPlanningProps {
  value: {
    schedule: WeeklySchedule;
  };
  onChange: (planning: { schedule: WeeklySchedule }) => void;
}

function PractitionerPlanning({ value, onChange }: PractitionerPlanningProps) {
  const updateSchedule = (schedule: WeeklySchedule) => {
    onChange({ schedule });
  };

  const addSlot = (day: DayKey) => {
    const currentSlots = value.schedule[day] || [];
    const defaultSlot: TimeSlot = currentSlots.length === 0
      ? { open: '09:00', close: '12:00' }
      : { open: '14:00', close: '18:00' };

    updateSchedule({
      ...value.schedule,
      [day]: [...currentSlots, defaultSlot],
    });
  };

  const updateSlot = (
    day: DayKey,
    index: number,
    field: keyof TimeSlot,
    nextValue: string,
  ) => {
    const currentSlots = value.schedule[day] || [];
    const nextSlots = currentSlots.map((slot, slotIndex) => (
      slotIndex === index
        ? { ...slot, [field]: nextValue }
        : slot
    ));

    updateSchedule({
      ...value.schedule,
      [day]: nextSlots,
    });
  };

  const removeSlot = (day: DayKey, index: number) => {
    const currentSlots = value.schedule[day] || [];
    const nextSlots = currentSlots.filter((_, slotIndex) => slotIndex !== index);
    const nextSchedule = { ...value.schedule };

    if (nextSlots.length === 0) {
      delete nextSchedule[day];
    } else {
      nextSchedule[day] = nextSlots;
    }

    updateSchedule(nextSchedule);
  };

  return (
    <div className="card">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-textPrimary">Disponibilités hebdomadaires</h3>
        <p className="mt-1 text-sm text-textSecondary">
          Ajoutez un ou plusieurs créneaux pour chaque jour.
        </p>
      </div>

      <div className="divide-y divide-gray-200">
        {planningDays.map(({ key, label }) => {
          const slots = value.schedule[key] || [];

          return (
            <div key={key} className="py-5 first:pt-0 last:pb-0">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-[140px_1fr]">
                <div>
                  <div className="font-medium text-textPrimary">{label}</div>
                  {slots.length === 0 && (
                    <div className="mt-1 text-sm text-gray-500">Indisponible</div>
                  )}
                </div>

                <div className="space-y-3">
                  {slots.map((slot, index) => (
                    <div
                      key={`${key}-${index}`}
                      className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:flex-row sm:items-end"
                    >
                      <div className="flex-1">
                        <label className="mb-1 block text-xs font-medium text-gray-600">De</label>
                        <TimeInput
                          value={slot.open}
                          onChange={(value) => updateSlot(key, index, 'open', value)}
                          className="input-field w-full"
                        />
                      </div>

                      <div className="flex-1">
                        <label className="mb-1 block text-xs font-medium text-gray-600">À</label>
                        <TimeInput
                          value={slot.close}
                          onChange={(value) => updateSlot(key, index, 'close', value)}
                          className="input-field w-full"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => removeSlot(key, index)}
                        className="inline-flex h-10 items-center justify-center rounded-lg border border-red-200 bg-white px-3 text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
                        title="Supprimer ce créneau"
                        aria-label={`Supprimer le créneau ${index + 1} du ${label.toLowerCase()}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => addSlot(key)}
                    className="inline-flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                  >
                    <Plus className="h-4 w-4" />
                    Ajouter un créneau à ce jour
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface PractitionerFormData {
  firstName: string;
  lastName: string;
  artistName: string;
  code: string;
  email: string;
  phone: string;
  password?: string;
  bio: string;
  experience: number;
  specialties: Array<{
    category: 'TATTOO' | 'PIERCING';
    styles: string[];
    experience: number;
    certifications: string[];
  }>;
  certifications: Array<{
    name: string;
    issuer: string;
    number: string;
    issuedAt: string;
    expiresAt?: string;
    verified: boolean;
  }>;
  availability: {
    schedule: WeeklySchedule;
    shopSchedules: Record<string, WeeklySchedule>;
    bookingWindow: number;
    minNotice: number;
  };
  pricing: {
    level: 'junior' | 'senior' | 'master' | 'guest';
    hourlyRate: number;
    minimumCharge: number;
    consultationFee: number;
  };
  isActive: boolean;
  onlineBookingEnabled: boolean;
}

const tattooStyles = [
  'Traditional', 'Realism', 'Watercolor', 'Geometric', 'Minimalist',
  'Blackwork', 'Dotwork', 'Tribal', 'Japanese', 'Biomechanical'
];

const piercingTypes = [
  'Ear Lobe', 'Ear Cartilage', 'Nose', 'Eyebrow', 'Lip',
  'Tongue', 'Navel', 'Nipple', 'Surface', 'Dermal'
];

export function PractitionerCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shops, currentShopId, setCurrentShopId } = useCurrentShop();
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState<PractitionerFormData>({
    firstName: '',
    lastName: '',
    artistName: '',
    code: '',
    email: '',
    phone: '',
    bio: '',
    experience: 0,
    specialties: [],
    certifications: [],
    availability: {
      schedule: {},
      shopSchedules: {},
      bookingWindow: 30,
      minNotice: 24,
    },
    pricing: {
      level: 'junior',
      hourlyRate: 80,
      minimumCharge: 60,
      consultationFee: 30
    },
    isActive: true,
    onlineBookingEnabled: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [phoneCountry, setPhoneCountry] = useState<SupportedPhoneCountry>('FR');
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [phoneE164, setPhoneE164] = useState<string | null>(null);
  const [phoneIsValid, setPhoneIsValid] = useState(false);
  const [selectedShopId, setSelectedShopId] = useState('');

  useEffect(() => {
    if (currentShopId && currentShopId !== ALL_SHOPS_ID) {
      if (selectedShopId !== currentShopId) setSelectedShopId(currentShopId);
      return;
    }
    if (!selectedShopId && shops.length === 1) {
      setSelectedShopId(shops[0].id);
    }
  }, [currentShopId, selectedShopId, shops]);

  const handleInputChange = (field: string, value: any) => {
    const keys = field.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current: any = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const addSpecialty = () => {
    setFormData(prev => ({
      ...prev,
      specialties: [
        ...prev.specialties,
        {
          category: 'TATTOO',
          styles: [],
          experience: 0,
          certifications: []
        }
      ]
    }));
  };

  const removeSpecialty = (index: number) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.filter((_, i) => i !== index)
    }));
  };

  const updateSpecialty = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.map((specialty, i) =>
        i === index ? { ...specialty, [field]: value } : specialty
      )
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShopId) {
      setError('Veuillez sélectionner un shop d\'affectation.');
      return;
    }
    if (!phoneDisplay.trim() || !phoneIsValid || !phoneE164) {
      setError('Le numéro de téléphone est invalide pour le pays sélectionné.');
      return;
    }
    if (!/^[A-Z0-9]{4}$/.test(formData.code.trim().toUpperCase())) {
      setError('Le code (4 caractères alphanumériques) est obligatoire.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setErrorDetails([]);

      const availability = {
        ...formData.availability,
        shopSchedules: {
          ...formData.availability.shopSchedules,
          [selectedShopId]: formData.availability.schedule,
        },
      };

      const response = await apiClient(`/api/admin/practitioners?shopId=${encodeURIComponent(selectedShopId)}`, {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          availability,
          code: formData.code.trim().toUpperCase(),
          phone: phoneE164,
          shopId: selectedShopId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        navigate('/practitioners');
      } else {
        setError(data.message || 'Échec de la création du praticien');
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
      }
    } catch (error) {
      console.error('Create practitioner error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/practitioners');
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleCancel}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour à la liste</span>
          </button>

          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>{t('navigation.practitioners.create')}</span>
              <HelpIcon
                docPath="06-gestion-praticiens#06-3-creation"
                tooltip="Documentation : Création d'un praticien"
              />
            </h1>
            <p className="text-textSecondary">{t('navigation.practitioners.createSubtitle')}</p>
          </div>
        </div>

        <button
          type="submit"
          form="practitioner-form"
          disabled={loading}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{loading ? 'Création...' : t('navigation.practitioners.create')}</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Form */}
      <form id="practitioner-form" onSubmit={handleSubmit} autoComplete="off" className="space-y-8">
        {/* Personal Information */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
              <User className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Informations Personnelles</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Prénom *
              </label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className="input-field"
                placeholder="Prénom de l'artiste"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Nom *
              </label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className="input-field"
                placeholder="Nom de l'artiste"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                <Star className="w-4 h-4 inline mr-1" />
                Nom d'artiste
              </label>
              <input
                type="text"
                value={formData.artistName}
                onChange={(e) => handleInputChange('artistName', e.target.value)}
                className="input-field"
                placeholder="Nom d'artiste (affiché aux clients)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Code *
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                className="input-field uppercase"
                maxLength={4}
                placeholder="JD01"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Téléphone *
              </label>
              <InternationalPhoneInput
                value={phoneDisplay}
                country={phoneCountry}
                onChange={(result) => {
                  setPhoneDisplay(result.displayValue);
                  setPhoneCountry(result.country);
                  setPhoneE164(result.e164Value);
                  setPhoneIsValid(result.isValid);
                  handleInputChange('phone', result.displayValue);
                  if (result.isValid) {
                    setError((prev) =>
                      prev?.startsWith('Le numéro de téléphone est invalide') ? null : prev,
                    );
                  }
                }}
                selectClassName="input-field"
                inputClassName="input-field"
                placeholder="+33 1 23 45 67 89"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                <Store className="w-4 h-4 inline mr-1" />
                Shop d'affectation *
              </label>
              <select
                required
                value={selectedShopId}
                onChange={(e) => {
                  const nextShopId = e.target.value;
                  setSelectedShopId(nextShopId);
                  if (nextShopId) {
                    setCurrentShopId(nextShopId);
                    setError((prev) =>
                      prev?.startsWith('Veuillez sélectionner un shop d\'affectation') ? null : prev,
                    );
                  }
                }}
                className="input-field"
              >
                <option value="">Sélectionner un shop</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email *
              </label>
              <input
                type="email"
                required
                autoComplete="off"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="input-field"
                placeholder="email@exemple.com"
              />
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Mot de passe (optionnel)
              </label>

              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={formData.password || ''}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="input-field pr-10"
                  placeholder="Si vide, une invitation sera envoyée par email"
                  minLength={8}
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  title={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>

              <p className="mt-1 text-xs text-textSecondary">
                Min. 8 caractères, majuscule, minuscule, chiffre et symbole. Si vide, le praticien recevra un email pour définir son mot de passe.
              </p>
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Biographie
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                rows={3}
                className="input-field"
                placeholder="Description de l'artiste, son style, son approche..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Années d'expérience *
              </label>
              <input
                type="number"
                required
                min="0"
                value={formData.experience}
                onChange={(e) => handleInputChange('experience', parseInt(e.target.value) || 0)}
                className="input-field"
                placeholder="5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Statut
              </label>
              <select
                value={formData.isActive ? 'active' : 'inactive'}
                onChange={(e) => handleInputChange('isActive', e.target.value === 'active')}
                className="input-field"
              >
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
            </div>

            <div className="flex items-end pb-1">
              <div>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.onlineBookingEnabled}
                    onChange={(e) => handleInputChange('onlineBookingEnabled', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-textPrimary">Réservation en ligne activée</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-7">
                  Permet aux clients de réserver directement en ligne
                </p>
              </div>
            </div>
          </div>
        </div>

        <PractitionerPlanning
          value={{
            schedule: formData.availability.schedule,
          }}
          onChange={(planning) => {
            setFormData((prev) => ({
              ...prev,
              availability: {
                ...prev.availability,
                schedule: planning.schedule,
              },
            }));
          }}
        />

        {/* Specialties */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-accent-100 rounded-lg">
                <Palette className="w-5 h-5 text-accent-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Spécialités</h2>
            </div>
            <button
              type="button"
              onClick={addSpecialty}
              className="btn-secondary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Ajouter une spécialité</span>
            </button>
          </div>

          {formData.specialties.map((specialty, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-textPrimary">Spécialité {index + 1}</h3>
                <button
                  type="button"
                  onClick={() => removeSpecialty(index)}
                  className="text-red-600 hover:text-red-700 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Catégorie
                  </label>
                  <select
                    value={specialty.category}
                    onChange={(e) => updateSpecialty(index, 'category', e.target.value)}
                    className="input-field"
                  >
                    <option value="TATTOO">Tatouage</option>
                    <option value="PIERCING">Piercing</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Expérience (années)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={specialty.experience}
                    onChange={(e) => updateSpecialty(index, 'experience', parseInt(e.target.value) || 0)}
                    className="input-field"
                  />
                </div>

                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Styles
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                    {(specialty.category === 'TATTOO' ? tattooStyles : piercingTypes).map((style) => (
                      <label key={style} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={specialty.styles.includes(style)}
                          onChange={(e) => {
                            const newStyles = e.target.checked
                              ? [...specialty.styles, style]
                              : specialty.styles.filter(s => s !== style);
                            updateSpecialty(index, 'styles', newStyles);
                          }}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-textPrimary">{style}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {formData.specialties.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Palette className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>Aucune spécialité ajoutée</p>
              <p className="text-sm">Cliquez sur "Ajouter une spécialité" pour commencer</p>
            </div>
          )}
        </div>

        {/* Pricing */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
              <Star className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Tarification</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Niveau
              </label>
              <select
                value={formData.pricing.level}
                onChange={(e) => handleInputChange('pricing.level', e.target.value)}
                className="input-field"
              >
                <option value="junior">Junior</option>
                <option value="senior">Senior</option>
                <option value="master">Master</option>
                <option value="guest">Invité</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Tarif horaire (€)
              </label>
              <input
                type="number"
                min="0"
                step="5"
                value={formData.pricing.hourlyRate}
                onChange={(e) => handleInputChange('pricing.hourlyRate', parseFloat(e.target.value) || 0)}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Tarif minimum (€)
              </label>
              <input
                type="number"
                min="0"
                step="5"
                value={formData.pricing.minimumCharge}
                onChange={(e) => handleInputChange('pricing.minimumCharge', parseFloat(e.target.value) || 0)}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Consultation (€)
              </label>
              <input
                type="number"
                min="0"
                step="5"
                value={formData.pricing.consultationFee}
                onChange={(e) => handleInputChange('pricing.consultationFee', parseFloat(e.target.value) || 0)}
                className="input-field"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
